/**
 * The deck-view beacon (A7Y-4448): the contract between the deck SPA and the
 * admin worker for per-session slide tracking.
 *
 * The SPA posts one beacon per slide visit to `DECK_VIEW_BEACON_PATH`; the
 * worker validates it with `parseDeckViewBeacon`, attaches the edge's geodata
 * and writes one Workers Analytics Engine data point (`worker/deck-view-record.ts`).
 * Shared by both sides, like `investor-host.ts`, so the two cannot disagree on
 * the path or the field set.
 */

/**
 * Under /era so `run_worker_first` in wrangler.jsonc carries it to the worker
 * on the ir host; the leading underscore keeps it out of the deck's own
 * `/era/<round>/<deck>` namespace.
 */
export const DECK_VIEW_BEACON_PATH = '/era/_view';

/** Why a slide visit ended. */
export type DeckViewReason =
  /** The viewer moved to another slide. */
  | 'advance'
  /** The tab went to the background; a new visit starts when it returns. */
  | 'hidden'
  /** The page was unloaded (pagehide) or the deck unmounted. */
  | 'leave';

export type DeckViewMode = 'desktop' | 'mobile';

export interface DeckViewBeacon {
  /** Schema version, for a future incompatible change. */
  v: 1;
  /** One id per deck page load — the viewing session. */
  session: string;
  /** The versioned deck slug, e.g. `series-seed-primer-v5`. */
  deck: string;
  /** Zero-based slide index. */
  slide: number;
  slideId: string;
  /** Slides in the deck, so a report can say "reached 12 of 24". */
  slides: number;
  mode: DeckViewMode;
  reason: DeckViewReason;
  /** Milliseconds the slide was visible during this visit. */
  dwellMs: number;
  /** Visit sequence within the session, from 1. */
  seq: number;
}

const SESSION_ID = /^[A-Za-z0-9_-]{8,64}$/;
const SLUG = /^[a-z0-9][a-z0-9-]{0,79}$/;
const REASONS: ReadonlySet<string> = new Set<DeckViewReason>(['advance', 'hidden', 'leave']);
const MODES: ReadonlySet<string> = new Set<DeckViewMode>(['desktop', 'mobile']);
const MAX_SLIDES = 500;
/** A visit longer than a day is a tab left open, not a reading. */
export const MAX_DWELL_MS = 24 * 60 * 60 * 1000;

function int(value: unknown, min: number, max: number): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const n = Math.round(value);
  return n >= min && n <= max ? n : null;
}

/** A validated beacon, or null for anything the worker must not store. */
export function parseDeckViewBeacon(input: unknown): DeckViewBeacon | null {
  if (!input || typeof input !== 'object') return null;
  const raw = input as Record<string, unknown>;
  if (raw.v !== 1) return null;
  const { session, deck, slideId, mode, reason } = raw;
  if (typeof session !== 'string' || !SESSION_ID.test(session)) return null;
  if (typeof deck !== 'string' || !SLUG.test(deck)) return null;
  if (typeof slideId !== 'string' || !SLUG.test(slideId)) return null;
  if (typeof mode !== 'string' || !MODES.has(mode)) return null;
  if (typeof reason !== 'string' || !REASONS.has(reason)) return null;
  const slides = int(raw.slides, 1, MAX_SLIDES);
  const slide = slides === null ? null : int(raw.slide, 0, slides - 1);
  const dwellMs = int(raw.dwellMs, 0, MAX_DWELL_MS);
  const seq = int(raw.seq, 1, 100_000);
  if (slides === null || slide === null || dwellMs === null || seq === null) return null;
  return {
    v: 1,
    session,
    deck,
    slide,
    slideId,
    slides,
    mode: mode as DeckViewMode,
    reason: reason as DeckViewReason,
    dwellMs,
    seq,
  };
}
